import { useEffect, useRef, useState, type FormEvent } from "react";
import { Link } from "react-router";
import type { PublicQuestion, QuestionFeedback } from "@book2class/shared/learning";
import { ReportButton } from "../../components/report";
import { api, ApiError } from "../../api";
import { Markdown } from "../../components/markdown";

// ─────────────────────────── Resumo visual ───────────────────────────

export type Visual = {
  kind: "mindmap" | "steps" | "timeline" | "comparison" | "cycle";
  title: string;
  nodes: Array<{ label: string; detail?: string }>;
};

const VISUAL_LABEL: Record<Visual["kind"], string> = {
  mindmap: "Mapa mental",
  steps: "Passo a passo",
  timeline: "Linha do tempo",
  comparison: "Comparação",
  cycle: "Ciclo",
};

/** Resumo visual renderizado a partir do roteiro gerado (sem bibliotecas de diagramas). */
export function VisualSummary({ visual }: { visual: Visual | null | undefined }) {
  if (!visual?.nodes?.length) return null;
  const { kind, nodes } = visual;
  return (
    <figure className={`visual visual-${kind}`} aria-label={`${VISUAL_LABEL[kind]}: ${visual.title}`}>
      <figcaption>
        <span className="eyebrow">Resumo visual · {VISUAL_LABEL[kind]}</span>
        <strong>{visual.title}</strong>
      </figcaption>
      {kind === "mindmap" ? (
        <div className="mm">
          <div className="mm-center">{visual.title}</div>
          <ul className="mm-branches">
            {nodes.map((n, i) => (
              <li key={i}>
                <span>{n.label}</span>
                {n.detail && <small>{n.detail}</small>}
              </li>
            ))}
          </ul>
        </div>
      ) : kind === "comparison" ? (
        <div className="cmp">
          {nodes.map((n, i) => (
            <div key={i} className="cmp-col">
              <strong>{n.label}</strong>
              {n.detail && <p>{n.detail}</p>}
            </div>
          ))}
        </div>
      ) : (
        <ol className={kind === "timeline" ? "tl" : kind === "cycle" ? "cy" : "st"}>
          {nodes.map((n, i) => (
            <li key={i}>
              <span className="vn">{i + 1}</span>
              <div>
                <strong>{n.label}</strong>
                {n.detail && <p>{n.detail}</p>}
              </div>
            </li>
          ))}
        </ol>
      )}
    </figure>
  );
}

// ─────────────────────────── Questões ───────────────────────────

type Answers = Record<string, unknown>;

function QuestionInput({
  q,
  value,
  onChange,
  fb,
}: {
  q: PublicQuestion;
  value: unknown;
  onChange: (v: unknown) => void;
  fb?: QuestionFeedback;
}) {
  const locked = Boolean(fb);
  if (q.kind === "multiple_choice")
    return (
      <div className="choices" role="radiogroup">
        {q.options.map((o) => {
          const chosen = value === o.id;
          const isRight = fb && fb.correctAnswer === o.id;
          return (
            <label
              key={o.id}
              className={`choice${chosen ? " chosen" : ""}${fb ? (isRight ? " right" : chosen ? " wrong" : "") : ""}`}
            >
              <input
                type="radio"
                name={q.id}
                disabled={locked}
                checked={chosen}
                onChange={() => onChange(o.id)}
              />
              <span className="choice-letter">{o.id.toUpperCase()}</span>
              <span>
                {o.text}
                {fb && (chosen || isRight) && fb.optionExplanations?.[o.id] && (
                  <small className="choice-why">{fb.optionExplanations[o.id]}</small>
                )}
              </span>
            </label>
          );
        })}
      </div>
    );
  if (q.kind === "true_false")
    return (
      <div className="tf">
        {[true, false].map((v) => (
          <button
            key={String(v)}
            type="button"
            disabled={locked}
            className={`btn ${value === v ? "" : "ghost"}${fb && fb.correctAnswer === v ? " right" : ""}${fb && value === v && !fb.correct ? " wrong" : ""}`}
            aria-pressed={value === v}
            onClick={() => onChange(v)}
          >
            {v ? "Verdadeiro" : "Falso"}
          </button>
        ))}
      </div>
    );
  const chosen = (value as string[] | undefined) ?? q.left.map(() => "");
  return (
    <div className="matching">
      {q.left.map((left, i) => (
        <div key={i} className="match-row">
          <span className="match-left">{left}</span>
          <select
            aria-label={`Correspondência de ${left}`}
            disabled={locked}
            value={chosen[i] ?? ""}
            className={fb ? ((fb.correctAnswer as string[])[i] === chosen[i] ? "right" : "wrong") : ""}
            onChange={(e) => onChange(chosen.map((c, j) => (j === i ? e.target.value : c)))}
          >
            <option value="">Escolha…</option>
            {q.right.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          {fb && (fb.correctAnswer as string[])[i] !== chosen[i] && (
            <small className="choice-why">Correto: {(fb.correctAnswer as string[])[i]}</small>
          )}
        </div>
      ))}
    </div>
  );
}

/** Aplica um conjunto de questões e mostra a correção feita pelo servidor. */
export function QuizRunner({
  questions,
  submit,
  submitLabel = "Corrigir",
  onDone,
  allowRetry = true,
  reportCourseId,
}: {
  /** Quando informado, cada questão ganha o botão “Relatar problema”. */
  reportCourseId?: string;
  questions: PublicQuestion[];
  submit: (
    answers: Array<{ questionId: string; answer: unknown }>,
  ) => Promise<{ score: number; feedback: QuestionFeedback[] }>;
  submitLabel?: string;
  onDone?: (result: { score: number; feedback: QuestionFeedback[] }) => void;
  allowRetry?: boolean;
}) {
  const [answers, setAnswers] = useState<Answers>({});
  const [result, setResult] = useState<{ score: number; feedback: QuestionFeedback[] } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const answered = questions.filter((q) => {
    const a = answers[q.id];
    return q.kind === "matching" ? Array.isArray(a) && a.every(Boolean) : a !== undefined;
  }).length;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const r = await submit(questions.map((q) => ({ questionId: q.id, answer: answers[q.id] ?? null })));
      setResult(r);
      onDone?.(r);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Não foi possível corrigir agora.");
    } finally {
      setBusy(false);
    }
  }

  const fbOf = (id: string) => result?.feedback.find((f) => f.questionId === id);
  return (
    <form className="quiz" onSubmit={onSubmit}>
      {questions.map((q, i) => {
        const fb = fbOf(q.id);
        return (
          <fieldset key={q.id} className={`question${fb ? (fb.correct ? " ok" : " ko") : ""}`}>
            <legend>
              <span className="q-num">{i + 1}</span> {q.prompt}
            </legend>
            {reportCourseId && (
              <div className="q-report">
                <ReportButton
                  courseId={reportCourseId}
                  itemType="question"
                  itemId={q.id}
                  itemLabel={q.prompt}
                  compact
                />
              </div>
            )}
            <QuestionInput
              q={q}
              value={answers[q.id]}
              fb={fb}
              onChange={(v) => setAnswers({ ...answers, [q.id]: v })}
            />
            {fb && (
              <p className={`q-feedback ${fb.correct ? "ok" : "ko"}`}>
                <strong>{fb.correct ? "Correto." : "Ainda não."}</strong> {fb.explanation}
              </p>
            )}
          </fieldset>
        );
      })}
      {error && <p className="alert error">{error}</p>}
      {result ? (
        <div className="quiz-result">
          <div>
            <div className="eyebrow">Resultado</div>
            <strong className="score">{result.score}%</strong>
            <span className="segment-meta">
              {result.feedback.filter((f) => f.correct).length} de {result.feedback.length} corretas
            </span>
          </div>
          {allowRetry && (
            <button
              type="button"
              className="btn ghost"
              onClick={() => {
                setResult(null);
                setAnswers({});
              }}
            >
              Refazer
            </button>
          )}
        </div>
      ) : (
        <div className="actions">
          <span className="segment-meta" style={{ marginRight: "auto" }}>
            {answered} de {questions.length} respondidas
          </span>
          <button className="btn" disabled={busy || answered === 0}>
            {busy ? "Corrigindo…" : submitLabel}
          </button>
        </div>
      )}
    </form>
  );
}

// ─────────────────────────── Assistente de dúvidas ───────────────────────────

type AssistantItem = {
  id: string;
  question: string;
  answer: string | null;
  outOfScope: boolean;
  references: Array<{ lessonId: string; title: string }>;
};

let assistantFlag: Promise<boolean> | null = null;
/** O assistente aparece só quando está ligado no servidor (ASSISTANT_ENABLED=true). */
export function AssistantPanel({ courseId }: { courseId: string }) {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    assistantFlag ??= api
      .get<{ assistantEnabled?: boolean }>("/auth/config")
      .then((c) => Boolean(c.assistantEnabled))
      .catch(() => false);
    void assistantFlag.then(setEnabled);
  }, []);
  return enabled ? <AssistantPanelInner courseId={courseId} /> : null;
}

function AssistantPanelInner({ courseId }: { courseId: string }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<AssistantItem[]>([]);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [question, setQuestion] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    api
      .get<{ remaining: number; history: AssistantItem[] }>(`/learn/${courseId}/assistant`)
      .then((r) => {
        setItems(r.history);
        setRemaining(r.remaining);
      })
      .catch(() => undefined);
  }, [open, courseId]);
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [items, busy]);

  return (
    <>
      <button type="button" className="assistant-fab" onClick={() => setOpen(!open)} aria-expanded={open}>
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12z" />
        </svg>
        Dúvidas
      </button>
      {open && (
        <aside className="assistant card" aria-label="Assistente de dúvidas">
          <div className="assistant-head">
            <div>
              <strong>Assistente do curso</strong>
              <div className="segment-meta">
                Responde com base no material do curso
                {remaining !== null ? ` · ${remaining} pergunta(s) hoje` : ""}
              </div>
            </div>
            <button
              type="button"
              className="icon-btn small"
              aria-label="Fechar"
              onClick={() => setOpen(false)}
            >
              ×
            </button>
          </div>
          <div className="assistant-list" ref={listRef} aria-live="polite">
            {!items.length && !busy && (
              <p className="segment-meta">
                Pergunte sobre qualquer tema do curso. Ex.: “Qual a diferença entre lucro e caixa?”
              </p>
            )}
            {items.map((it) => (
              <div key={it.id} className="assistant-turn">
                <p className="bubble me">{it.question}</p>
                <div className={`bubble bot${it.outOfScope ? " out" : ""}`}>
                  <Markdown text={it.answer ?? ""} className="reading small" />
                  {it.references.length > 0 && (
                    <div className="refs">
                      Veja:{" "}
                      {it.references.map((r, i) => (
                        <span key={r.lessonId}>
                          {i > 0 && " · "}
                          <Link
                            to={`/aluno/cursos/${courseId}/aulas/${r.lessonId}`}
                            onClick={() => setOpen(false)}
                          >
                            {r.title}
                          </Link>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && <p className="bubble bot typing">Pensando…</p>}
          </div>
          {error && <p className="alert error">{error}</p>}
          <form
            className="assistant-form"
            onSubmit={async (e) => {
              e.preventDefault();
              if (question.trim().length < 3) return;
              setBusy(true);
              setError("");
              try {
                const r = await api.post<AssistantItem & { remaining: number }>(
                  `/learn/${courseId}/assistant`,
                  { question },
                );
                setItems([...items, r]);
                setRemaining(r.remaining);
                setQuestion("");
              } catch (err) {
                setError(err instanceof ApiError ? err.message : "O assistente está indisponível.");
              } finally {
                setBusy(false);
              }
            }}
          >
            <input
              type="text"
              aria-label="Sua dúvida"
              placeholder="Escreva sua dúvida…"
              value={question}
              maxLength={500}
              onChange={(e) => setQuestion(e.target.value)}
            />
            <button className="btn" disabled={busy || remaining === 0 || question.trim().length < 3}>
              Enviar
            </button>
          </form>
        </aside>
      )}
    </>
  );
}

export function LearnCrumb({ courseId, title, here }: { courseId: string; title: string; here?: string }) {
  return (
    <div className="eyebrow">
      <Link to="/aluno">Meus cursos</Link> / <Link to={`/aluno/cursos/${courseId}`}>{title}</Link>
      {here ? ` / ${here}` : ""}
    </div>
  );
}
